import { v4 as uuid } from 'uuid';
import { cloneDeep } from 'lodash';

import type { SupportedLanguage } from '../../../translate';
import { CUSTOM_SECTION_TITLE } from '../types/constants';
import type {
  EpiComposition,
  EpiText,
  EpiDocumentData,
  EpiDocumentType,
  EpiIdmpLink,
  EpiIdmpRef,
  EpiProductFamilyRef,
} from '../types/types';
import { buildEmptyComposition, getDocumentPropertyNames } from './schemaUtils';

// ============================================================================
// PER-LANGUAGE TEXT
// ============================================================================

/** An empty value for every supported language. */
export const emptyText = (): EpiText => ({ sr: '', en: '' });

/**
 * Reads one language. Deliberately **no fallback to another language**: the two
 * are parallel copies, and quietly printing Serbian where English is missing
 * would misrepresent a regulatory document. Missing reads as empty, and
 * whether that is acceptable is a validation-rule and export question.
 */
export const text = (
  value: EpiText | undefined,
  lang: SupportedLanguage
): string => value?.[lang] ?? '';

/** Writes one language, leaving the others untouched. */
export const setText = (
  value: EpiText | undefined,
  lang: SupportedLanguage,
  next: string
): EpiText => ({ ...emptyText(), ...(value ?? {}), [lang]: next });

// ============================================================================
// DISPLAY NAMES
// ============================================================================

/**
 * Resolves a model instance's `name` for display.
 *
 * It is not always a string: IDMP declares `name` as `{sr, en}` with `sr`
 * required, so rendering it raw throws ("Objects are not valid as a React
 * child"). Prefers the current UI language, then Serbian, then English, then
 * any non-empty string it can find.
 */
export function resolveInstanceName(
  value: unknown,
  lang: SupportedLanguage
): string | undefined {
  if (typeof value === 'string') return value || undefined;
  if (!value || typeof value !== 'object') return undefined;

  const byLang = value as Record<string, unknown>;
  const isText = (v: unknown): v is string =>
    typeof v === 'string' && v.trim().length > 0;

  return (
    [byLang[lang], byLang.sr, byLang.en].find(isText) ??
    Object.values(byLang).find(isText)
  );
}

/**
 * The IDMP's strength, when it carries one. Products on one ePI almost always
 * share the family name, so the strength is the part that tells them apart —
 * and "Mounjaro 2,5 mg rastvor za injekciju u bočici" does not fit on a chip.
 * A plain string on the instance, not a per-language object.
 */
export function idmpStrength(data: unknown): string | undefined {
  const value = (data as Record<string, unknown> | undefined)?.strengthValue;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * What to show for a section in the language being read. A custom section is
 * titled in the language it was written in and nowhere else — whether it was
 * imported or added in the editor — so the other language has a row with
 * nothing in it. A placeholder, never stored: the export prints the title as
 * the section's heading, and "Custom section" is not one.
 */
export function sectionLabel(title: string): string {
  return title.trim() || CUSTOM_SECTION_TITLE;
}

// ============================================================================
// REFERENCE NORMALIZATION
// ============================================================================

/**
 * Prepares a document for the save payload. Two jobs.
 *
 * **Reference enrichment.** The BE enriches EAGER references when it reads an
 * instance, adding `data`, `modelVersionId` and friends. Written back, those
 * are rejected — `ReferenceValidatorService` allows only `id`, `version` and
 * `modelId` on a MODEL_INSTANCE reference, and a reference that arrives
 * changed is validated rather than skipped. The editor keeps the enriched form
 * in state so it can show names without a fetch, so it is stripped here.
 *
 * **Fields the schema no longer declares.** The editor round-trips whatever
 * the instance holds, so a field left over from an older model keeps coming
 * back and failing validation. `idmpReference` is the case that matters: it
 * bound an ePI to a single product before the family model. Sending it as
 * `null` is what removes it — the actions engine reads an explicit null on a
 * present key as a DELETE, and validation runs on the merged result, so the
 * field is gone by the time the schema is checked. Omitting it would simply
 * leave the stored value in place.
 */
export function normalizeForSave(data: EpiDocumentData): EpiDocumentData {
  const cleanIdmp = (r: EpiIdmpRef): EpiIdmpRef => ({
    id: r.id,
    modelId: r.modelId,
    // One of the three fields a reference may carry, and the one that says
    // which version of the product this ePI documents. Dropping it would
    // silently re-point the ePI at whatever the latest becomes.
    version: r.version,
    ...(r.toBeDeleted ? { toBeDeleted: true } : {}),
  });
  // A composition points at the root set rather than repeating it, so its
  // entries carry `id` alone. They arrive carrying more than that — the page
  // hydrates every `{id}`-only object from elsewhere in the instance before
  // the editor sees it — and `modelId`/`version` on an internal reference is
  // a field the schema does not declare.
  const cleanIdmpLink = (r: EpiIdmpLink): EpiIdmpLink => ({
    id: r.id,
    ...(r.toBeDeleted ? { toBeDeleted: true } : {}),
  });
  const cleanCompositions = (comps: EpiComposition[]): EpiComposition[] =>
    (comps ?? []).map(c => ({
      ...c,
      medicinalProducts: (c.medicinalProducts ?? []).map(cleanIdmpLink),
    }));

  const declared = new Set(getDocumentPropertyNames());
  const removals = Object.fromEntries(
    Object.keys(data)
      .filter(k => !declared.has(k))
      .map(k => [k, null])
  );

  return {
    ...data,
    ...removals,
    ...(data.productFamily
      ? {
          productFamily: {
            id: data.productFamily.id,
            modelId: data.productFamily.modelId,
            version: data.productFamily.version,
          },
        }
      : {}),
    medicinalProducts: (data.medicinalProducts ?? []).map(cleanIdmp),
    smpc: cleanCompositions(data.smpc),
    annexII: cleanCompositions(data.annexII),
    labelling: cleanCompositions(data.labelling),
    packageLeaflet: cleanCompositions(data.packageLeaflet),
  };
}

// ============================================================================
// COMPOSITION UTILITIES
// ============================================================================

/**
 * Deep-clone a composition and assign it a new copy title
 */
export function cloneComposition(
  src: EpiComposition,
  allTitles: string[],
  lang: SupportedLanguage
): EpiComposition {
  const clone = cloneDeep(src);
  clone.id = uuid();
  clone.title = setText(
    src.title,
    lang,
    generateCopyTitle(text(src.title, lang), allTitles)
  );
  return clone;
}

/**
 * Generate a new title with " – Kopija" suffix, incrementing existing copies.
 */
export function generateCopyTitle(
  original: string,
  allTitles: string[]
): string {
  const root = original.replace(/ – Kopija(?: \(\d+\))?$/, '');
  const esc = root.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  const regex = new RegExp(`^${esc} – Kopija(?: \\((\\d+)\\))?$`);
  let max = 0;

  allTitles.forEach(t => {
    const m = t.match(regex);
    if (m) {
      const idx = m[1] ? parseInt(m[1], 10) : 1;
      max = Math.max(max, idx);
    }
  });

  const next = max + 1;
  return next === 1 ? `${root} – Kopija` : `${root} – Kopija (${next})`;
}

// ============================================================================
// DOCUMENT DATA UTILITIES
// ============================================================================

/**
 * Creates a new empty EpiDocumentData with default compositions.
 */
/**
 * A new ePI: one composition per document type, each covering every IDMP the
 * ePI was created with. The user splits them apart afterwards by peeling.
 */
export function createEmptyDocument(
  name: string,
  medicinalProducts: EpiIdmpRef[],
  productFamily?: Pick<EpiProductFamilyRef, 'id' | 'modelId' | 'version'>
): EpiDocumentData {
  const links = medicinalProducts.map(({ id }) => ({ id }));
  return {
    name,
    status: 'current',
    productFamily,
    medicinalProducts,
    smpc: [buildEmptyComposition('smpc', links)],
    annexII: [buildEmptyComposition('annexII', links)],
    labelling: [buildEmptyComposition('labelling', links)],
    packageLeaflet: [buildEmptyComposition('packageLeaflet', links)],
  };
}

/**
 * Gets compositions for a specific tab/document type from the full document data.
 */
export function getCompositionsForType(
  data: EpiDocumentData,
  docType: EpiDocumentType
): EpiComposition[] {
  return data[docType] ?? [];
}

/**
 * Merges compositions for a specific tab back into the full document data.
 */
export function mergeCompositionsForType(
  data: EpiDocumentData,
  docType: EpiDocumentType,
  compositions: EpiComposition[]
): EpiDocumentData {
  return { ...data, [docType]: compositions };
}
